/**
 * Google Analytics mantido em paralelo ao Umami.
 * Usa o "gtag.js" direto, e não o SDK do Firebase.
 * As propriedades que recebem os dados não mudaram.
 */
import { isMetricsProd } from 'config/util'

import { AnalyticsEventParams } from './model-analytics'

const MEASUREMENT_ID_PROD = 'G-RXTX8LJCDF'
const MEASUREMENT_ID_DEV = 'G-43LSX16PMY'
const SCRIPT_ID = 'ga-analytics'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

function measurementId(): string {
  return isMetricsProd() ? MEASUREMENT_ID_PROD : MEASUREMENT_ID_DEV
}

export function init() {
  if (document.getElementById(SCRIPT_ID)) return

  injectScript()
  configureGtag()
}

function injectScript() {
  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId()}`
  document.head.appendChild(script)
}

function configureGtag() {
  const dataLayer = window.dataLayer ?? []
  window.dataLayer = dataLayer
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments)
  }

  window.gtag('js', new Date())
  window.gtag('config', measurementId(), { send_page_view: false })
}

export function remove() {
  document.getElementById(SCRIPT_ID)?.remove()
  delete window.gtag
  delete window.dataLayer

  apagarCookie('_ga')
  apagarCookie(`_ga_${measurementId().replace('G-', '')}`)
}

/**
 * O gtag grava os cookies em "path=/" e no domínio registrável, então a exclusão precisa repetir
 * esses atributos, senão o cookie sobrevive. Como o domínio registrável não é conhecido aqui,
 * tenta-se cada sufixo do host; sufixos públicos (ex: "gov.br") são ignorados pelo navegador.
 */
function apagarCookie(nome: string) {
  const expiracao = `${nome}=; Max-Age=0; path=/`
  const partes = window.location.hostname.split('.')

  document.cookie = expiracao
  for (let i = 0; i < partes.length - 1; i++) {
    document.cookie = `${expiracao}; domain=${partes.slice(i).join('.')}`
  }
}

export function logEvent(eventName: string, eventParams: AnalyticsEventParams | undefined, rota: string) {
  window.gtag?.('event', eventName, { ...eventParams, page_location: getPageLocation(rota) })
}

export function logPageview(rota: string) {
  window.gtag?.('event', 'page_view', { page_location: getPageLocation(rota) })
}

function getPageLocation(rota: string): string {
  return new URL(rota, window.location.origin).href
}

export function setUserId(id: string) {
  window.gtag?.('set', { user_id: id })
}

export function setUserProperties(properties: AnalyticsEventParams) {
  window.gtag?.('set', 'user_properties', properties)
}
